import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Spinner,
} from "@getgreenline/blaze-ui";
import { Plus, SearchX, TriangleAlert, UsersRound } from "lucide-react";
import { useState } from "react";
import { AddUserDialog } from "@/components/backoffice/add-user-dialog";
import { UsersBrandsTable } from "@/components/backoffice/users-brands-table";
import { StaffAccessGate } from "@/components/shared/staff-access-gate";
import {
  useBrandUsers,
  useOnboardedBrands,
} from "@/hooks/use-staff-brand-users";
import type { BrandUserListItem } from "@/services/staff-brand-users.service";

function UsersState({
  users,
  isLoading,
  isError,
}: {
  users: BrandUserListItem[];
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <Empty className="tw:border">
        <EmptyMedia>
          <Spinner className="tw:size-8" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>Loading users</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <TriangleAlert className="tw:size-4" />
        <AlertTitle>Users could not be loaded</AlertTitle>
        <AlertDescription>
          Try again. If the problem continues, contact support.
        </AlertDescription>
      </Alert>
    );
  }

  if (users.length === 0) {
    return (
      <Empty className="tw:border">
        <EmptyMedia variant="icon">
          <UsersRound className="tw:size-5" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>No users assigned</EmptyTitle>
        </EmptyHeader>
        <EmptyContent>
          Users will appear here after they are assigned to an onboarded brand.
        </EmptyContent>
      </Empty>
    );
  }

  if (users.length === 0) {
    return (
      <Empty className="tw:border">
        <EmptyMedia variant="icon">
          <SearchX className="tw:size-5" />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>No matching users</EmptyTitle>
        </EmptyHeader>
        <EmptyContent>Try a different email address or brand.</EmptyContent>
      </Empty>
    );
  }

  return <UsersBrandsTable users={users} />;
}

export function BackofficeUsersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const brandUsers = useBrandUsers({ query: searchQuery, page });
  const onboardedBrands = useOnboardedBrands();
  const users = brandUsers.data?.users ?? [];
  const assignableBrands = (onboardedBrands.data ?? []).map(
    ({ id, name, avatarImageUrl }) => ({ id, name, avatarImageUrl }),
  );

  return (
    <StaffAccessGate>
      <main className="tw:@container tw:flex tw:min-w-0 tw:flex-col tw:gap-6 tw:px-6 tw:py-8 sm:tw:px-8">
        <CardHeader className="tw:px-0 tw:pt-0">
          <CardTitle>
            <h1 className="tw:text-2xl tw:font-semibold">Users</h1>
          </CardTitle>
          <CardDescription className="tw:max-w-2xl">
            View users and the onboarded brands they can access.
          </CardDescription>
        </CardHeader>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:w-full tw:max-w-xl sm:tw:w-auto sm:tw:flex-1">
            <Input
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search users or brands"
              aria-label="Search brand users"
            />
          </div>
          <Button type="button" onClick={() => setIsAddUserOpen(true)}>
            <Plus className="tw:size-4" />
            Add user
          </Button>
        </div>

        <UsersState
          users={users}
          isLoading={brandUsers.isLoading || brandUsers.isDebouncing}
          isError={brandUsers.isError}
        />

        {page > 1 || brandUsers.data?.hasMore ? (
          <div className="tw:flex tw:justify-end tw:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((currentPage) => currentPage - 1)}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!brandUsers.data?.hasMore}
              onClick={() => setPage((currentPage) => currentPage + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}

        {isAddUserOpen ? (
          <AddUserDialog
            brands={assignableBrands}
            open
            onOpenChange={setIsAddUserOpen}
          />
        ) : null}
      </main>
    </StaffAccessGate>
  );
}
